import Phaser from 'phaser';
import type { ExpeditionScene } from './ExpeditionScene';

interface PartyChip {
  bg: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  morale: Phaser.GameObjects.Text;
  hpBar: Phaser.GameObjects.Graphics;
}

interface AbilityChip {
  bg: Phaser.GameObjects.Graphics;
  hotkey: Phaser.GameObjects.Text;
  name: Phaser.GameObjects.Text;
  cd: Phaser.GameObjects.Text;
}

export class HudScene extends Phaser.Scene {
  private depthText!: Phaser.GameObjects.Text;
  private killText!: Phaser.GameObjects.Text;
  private goldText!: Phaser.GameObjects.Text;
  private potionText!: Phaser.GameObjects.Text;
  private hungerText!: Phaser.GameObjects.Text;
  private hintText!: Phaser.GameObjects.Text;
  private panel!: Phaser.GameObjects.Graphics;
  private partyChips: PartyChip[] = [];
  private abilityChips: AbilityChip[] = [];

  constructor() { super({ key: 'HudScene', active: false }); }

  create(): void {
    this.partyChips = [];
    this.panel = this.add.graphics();
    this.redrawPanel();

    const fontHead: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'monospace', fontSize: '14px', color: '#f4ecd6',
    };
    const fontDim: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: 'monospace', fontSize: '11px', color: '#9a8f7a',
    };

    this.add.text(14, 10, 'WANDERBORNE', { ...fontHead, color: '#ffd54a' });

    this.depthText  = this.add.text(180, 12, 'DEPTH 1', fontHead);
    this.killText   = this.add.text(450, 12, 'KILLS 0', fontHead);
    this.goldText   = this.add.text(580, 12, 'GOLD 0', { ...fontHead, color: '#ffd54a' });
    this.potionText = this.add.text(700, 12, 'POTIONS 0', fontHead);
    this.hungerText = this.add.text(840, 12, 'HUNGER 100', fontHead);

    this.hintText = this.add.text(
      this.scale.width / 2,
      this.scale.height - 14,
      'Click move · [QWERT] abilities · [I] inventory · [1] potion · [2] ration · Yellow diamond to advance',
      { ...fontDim, color: '#c8bfa3' },
    );
    this.hintText.setOrigin(0.5, 0.5);

    const refresh = () => this.refresh();
    this.game.events.on('hud:update', refresh);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.game.events.off('hud:update', refresh);
      for (const c of this.partyChips) { c.bg.destroy(); c.label.destroy(); c.morale.destroy(); c.hpBar.destroy(); }
      this.partyChips = [];
      for (const c of this.abilityChips) { c.bg.destroy(); c.hotkey.destroy(); c.name.destroy(); c.cd.destroy(); }
      this.abilityChips = [];
    });
    this.refresh();
  }

  private redrawPanel(): void {
    this.panel.clear();
    this.panel.fillStyle(0x000000, 0.55);
    this.panel.fillRect(0, 0, this.scale.width, 38);
    this.panel.fillStyle(0x000000, 0.7);
    this.panel.fillRect(0, this.scale.height - 28, this.scale.width, 28);
  }

  private refresh(): void {
    const exp = this.scene.get('ExpeditionScene') as ExpeditionScene | undefined;
    if (!exp || !exp.getStatus) return;
    const s = exp.getStatus();
    const placeLabel = s.chunkKind === 'village' ? 'VILLAGE'
      : s.chunkKind === 'dungeon' ? (s.biome || 'cave').toUpperCase()
      : (s.biome || 'plains').toUpperCase();
    const counter = s.chunkKind === 'dungeon' ? `DEPTH ${s.depth}` : `DISTANCE ${s.distance}`;
    this.depthText.setText(`${counter} · ${placeLabel}`);
    this.killText.setText(`KILLS ${s.killed}`);
    this.goldText.setText(`GOLD ${s.gold}`);
    this.potionText.setText(`POTIONS ${s.potions}`);
    this.hungerText.setText(`HUNGER ${s.hunger}`);
    this.hungerText.setColor(s.hunger <= 25 ? '#ff7777' : s.hunger <= 50 ? '#ffb060' : '#f4ecd6');
    this.renderPartyChips(s.party);
    this.renderAbilityHotbar(s.abilities);
  }

  /**
   * Render one chip per party member down the left side under the top bar:
   *  [VANGUARD ███████   ]   (HP color shifts red when low)
   */
  private renderPartyChips(party: { name: string; hp: number; maxHp: number; role?: string; morale: number; moraleState: string; moraleColor: number }[]): void {
    while (this.partyChips.length < party.length) {
      const bg = this.add.graphics();
      const label = this.add.text(0, 0, '', { fontFamily: 'monospace', fontSize: '11px', color: '#f4ecd6' });
      const morale = this.add.text(0, 0, '', { fontFamily: 'monospace', fontSize: '10px', color: '#ffd54a' });
      const hpBar = this.add.graphics();
      this.partyChips.push({ bg, label, morale, hpBar });
    }
    while (this.partyChips.length > party.length) {
      const c = this.partyChips.pop()!;
      c.bg.destroy(); c.label.destroy(); c.morale.destroy(); c.hpBar.destroy();
    }

    const chipW = 200;
    const chipH = 36;
    const baseX = 14;
    const baseY = 50;
    const gap = 6;

    for (let i = 0; i < party.length; i++) {
      const p = party[i];
      const chip = this.partyChips[i];
      const x = baseX;
      const y = baseY + i * (chipH + gap);
      const ratio = Math.max(0, p.hp / p.maxHp);
      const dead = p.hp <= 0;
      const hpColor = dead ? 0x6b3a3a : ratio < 0.3 ? 0xc62828 : 0x4caf50;

      chip.bg.clear();
      chip.bg.fillStyle(0x000000, 0.55);
      chip.bg.fillRect(x, y, chipW, chipH);
      chip.bg.lineStyle(1, dead ? 0x6b3a3a : 0x3a3327, 1);
      chip.bg.strokeRect(x, y, chipW, chipH);

      chip.label.setPosition(x + 8, y + 4);
      chip.label.setText(`${p.name.toUpperCase()}${dead ? ' ✕' : ''}`);
      chip.label.setColor(dead ? '#6b3a3a' : '#f4ecd6');

      // Morale state, color-coded to band.
      chip.morale.setPosition(x + chipW - 8, y + 4);
      chip.morale.setOrigin(1, 0);
      chip.morale.setText(dead ? '' : p.moraleState);
      chip.morale.setColor('#' + p.moraleColor.toString(16).padStart(6, '0'));

      // HP bar.
      const barX = x + 8;
      const barY = y + 22;
      const barW = chipW - 16;
      const barH = 8;
      chip.hpBar.clear();
      chip.hpBar.fillStyle(0x14110e, 1);
      chip.hpBar.fillRect(barX - 1, barY - 1, barW + 2, barH + 2);
      chip.hpBar.fillStyle(hpColor, 1);
      chip.hpBar.fillRect(barX, barY, barW * ratio, barH);
      // HP text overlay.
      chip.label.setText(`${p.name.toUpperCase()}${dead ? ' ✕' : ''}  ${Math.max(0, p.hp)}/${p.maxHp}`);
    }
  }

  /**
   * Bottom-center QWER hotbar. Each chip:
   *   ┌──────────┐
   *   │ Q | Cleave │
   *   │   ▮ 4.2s   │   (cooldown sweep + remaining seconds)
   *   └──────────┘
   */
  private renderAbilityHotbar(abilities: { id: string; name: string; hotkey: string; cooldown: number; remaining: number; ready: boolean }[]): void {
    while (this.abilityChips.length < abilities.length) {
      const bg = this.add.graphics();
      const hotkey = this.add.text(0, 0, '', { fontFamily: 'monospace', fontSize: '14px', color: '#ffd54a' });
      const name = this.add.text(0, 0, '', { fontFamily: 'monospace', fontSize: '10px', color: '#f4ecd6' });
      const cd = this.add.text(0, 0, '', { fontFamily: 'monospace', fontSize: '10px', color: '#9a8f7a' });
      this.abilityChips.push({ bg, hotkey, name, cd });
    }
    while (this.abilityChips.length > abilities.length) {
      const c = this.abilityChips.pop()!;
      c.bg.destroy(); c.hotkey.destroy(); c.name.destroy(); c.cd.destroy();
    }

    const chipW = 100;
    const chipH = 50;
    const gap = 8;
    const totalW = abilities.length * chipW + (abilities.length - 1) * gap;
    const startX = (this.scale.width - totalW) / 2;
    const y = this.scale.height - chipH - 36; // above hint bar

    for (let i = 0; i < abilities.length; i++) {
      const a = abilities[i];
      const chip = this.abilityChips[i];
      const x = startX + i * (chipW + gap);
      const onCd = a.remaining > 0;

      chip.bg.clear();
      chip.bg.fillStyle(a.ready ? 0x1a1610 : 0x0f0e0c, 0.92);
      chip.bg.fillRect(x, y, chipW, chipH);
      chip.bg.lineStyle(1, a.ready ? 0xffd54a : 0x3a3327, 1);
      chip.bg.strokeRect(x, y, chipW, chipH);
      // Cooldown fill — sweeps from bottom up.
      if (onCd) {
        const ratio = Math.max(0, Math.min(1, a.remaining / a.cooldown));
        chip.bg.fillStyle(0x000000, 0.55);
        chip.bg.fillRect(x, y + chipH - chipH * ratio, chipW, chipH * ratio);
      }

      chip.hotkey.setText(a.hotkey);
      chip.hotkey.setPosition(x + 8, y + 6);
      chip.hotkey.setColor(a.ready ? '#ffd54a' : '#5a4f3a');

      chip.name.setText(a.name);
      chip.name.setPosition(x + 26, y + 9);
      chip.name.setColor(a.ready ? '#f4ecd6' : '#7a6f5a');

      chip.cd.setText(onCd ? `${a.remaining.toFixed(1)}s` : 'ready');
      chip.cd.setPosition(x + 8, y + chipH - 16);
      chip.cd.setColor(onCd ? '#c8bfa3' : '#4caf50');
    }
  }
}
